<template>
  <fieldset class="contact-phone"
            :class="{ 'app-embedded-fieldset': embedded }"><!-- using fieldset as verification code was expected in future release -->
    <legend v-if="titlePhone == 'Phone'">
      <span>{{ titlePhone }}<span>(xxx-xxx-xxxx)</span><span>(Message and data rates may apply.)</span></span>
    </legend>
    <legend v-else>
      <span>{{ titlePhone }}</span>
    </legend>
    <span class="app-contact-inputs">
      <ValidationForm ref="phoneForm">
        <div class="app-input-group app-contact-info" :class="{ 'app-invalid-group': invalidPhone || invalidPhoneNumChars }">
          <div class="app-phone-number">
            <Field name="myPhoneAC" rules="numeric|length:3" >
              <label for="userPhoneAC" class="sr-only">Area code</label>
              <input id="userPhoneAC" type="tel" ref="acFieldRef" v-model="myPhoneAC" @keyup="switchFocus"
              :readonly="editable" :disabled="editable">
            </Field> -
            <Field name="myPhoneF3" rules="numeric|length:3" >
              <label for="userPhoneF3" class="sr-only">First three digits</label>
              <input id="userPhoneF3" type="tel" ref="f3FieldRef" v-model="myPhoneF3" @keyup="switchFocus"
              :readonly="editable" :disabled="editable">
            </Field> -
            <Field name="myPhoneL4" rules="numeric|length:4">
              <label for="userPhoneL4" class="sr-only">Last four digits</label>
              <input id="userPhoneL4" type="tel" ref="l4FieldRef" v-model="myPhoneL4" @keyup="switchFocus"
              :readonly="editable" :disabled="editable">
              <p v-if="invalidPhoneNumChars">{{invalidPhoneMessage}}</p>
              <p v-else-if="invalidPhone">{{ invalidPhoneErrorMessage }}</p>
            </Field>
          </div>
        </div>
      </ValidationForm>
    </span>
  </fieldset>
</template>


<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Form as ValidationForm, Field, defineRule, useForm } from 'vee-validate';
import {  numeric } from '@vee-validate/rules'; 
import { usePhoneFormatter } from '@/composables/usePhoneFormatter'; 
import * as COMMONS from '../../store/_commons';

defineRule('length', (value, [limit]) => {
  if (!value || !value.length) {
    return true;
  }
  if (value.length !== limit) {
    return `This field must have ${limit} digits.`;
  }
  return true;
});

defineRule('numeric', numeric); 

const props = defineProps({
  embedded: {
    type: Boolean,
    required: false,
    default: false,
  },
  isModal: {
    type: Boolean,
    required: false,
    default: false,
  },
  verify: {
    type: Boolean,
    required: false,
    default: false,
  },
  editable: {
    type: Boolean,
    required: false,
    default: false,
  },
  titlePhone: {
    type: String,
    required: false,
    default: 'Phone',
  },
  showPhone: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['doResetVerify', 'doPhone']);

// Use the phone formatter composable
const { formatPhoneForWebapp } = usePhoneFormatter();

// State
const store = useStore();
const myPhoneAC = ref('');
const myPhoneF3 = ref('');
const myPhoneL4 = ref('');
const myPhone = ref('');
const invalidPhone = ref(false);
const phoneForm = ref(null);

// Template refs 
const acFieldRef = ref(null);
const f3FieldRef = ref(null);
const l4FieldRef = ref(null);

const { validate } = useForm();

// Computed
const invalidPhoneNumChars = computed(() => {
  const phoneNum = myPhoneAC.value + myPhoneF3.value + myPhoneL4.value;
  if (phoneNum.length === 0) {
    return false;
  }
  const pattern = /^\d+$/;
  return !pattern.test(phoneNum);
});

const personalInfo = computed(() => store.getters[COMMONS.GUEST_PERSONAL_INFO]);

const invalidPhoneMessage = computed(() => {
  const title = props.titlePhone.toLowerCase();
  
  if (title.includes('office phone')) {
    return 'Office phone number contains invalid characters';
  } else if (title.includes('office fax')) {
    return 'Office fax number contains invalid characters';
  } else if (title.includes('cell phone')) {
    return 'Cell phone number contains invalid characters';
  } else {
    return 'Phone number contains invalid characters';
  }
});

const invalidPhoneErrorMessage = computed(() => {
  const title = props.titlePhone.toLowerCase();
  
  if (title.includes('office phone')) {
    return 'Please enter a valid office phone number.';
  } else if (title.includes('office fax')) {
    return 'Please enter a valid office fax number.';
  } else if (title.includes('cell phone')) {
    return 'Please enter a valid cell phone number.';
  } else {
    return 'Please enter a valid phone number.';
  }
});

// Watchers
watch(myPhoneAC, () => {
  updateMyPhone('myPhoneAC');
});

watch(myPhoneF3, () => {
  updateMyPhone('myPhoneF3');
});

watch(myPhoneL4, () => {
  updateMyPhone('myPhoneL4');
});

watch(myPhone, async (newVal) => {
  if (newVal.length === 0 || newVal.length === 10) {
    await checkPhone();
  }
});

watch(() => props.verify, async (newVal) => {
  if (newVal) {
    await checkPhone();
  }
});

// Lifecycle Hooks
onMounted(() => {
  if (store.state.guestLogin.finalizationCart && store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart) {
    const info = store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart;
    if (info && info.phone && info.phone.length > 0) {
      const phoneNum = formatPhoneForWebapp(info.phone);
      if (phoneNum.length === 10) {
        myPhone.value = phoneNum;
        split(phoneNum);
      }
    }
  }

  if(props.showPhone) {
    const personalPhone = formatPhoneForWebapp(personalInfo.value.personalInformation && personalInfo.value.personalInformation.phone || '');
    if(personalPhone.length === 10) {
      myPhone.value = personalPhone;
      split(myPhone.value);
    }
  }
});

//Methods
const checkPhone = async () => {
  if (invalidPhoneNumChars.value) {
    invalidPhone.value = true;
    emit('doResetVerify');
    emit('doPhone', { phone: myPhone.value, isValid: false });
    return false;
  }

  const isValid = await hasValidPhone();
  invalidPhone.value = !isValid;

  if (!isValid) {
    emit('doResetVerify');
  }

  emit('doPhone', { phone: myPhone.value, isValid });

  return isValid;
};

const hasValidPhone = async () => {
  if (invalidPhoneNumChars.value) { 
    return false;
  }
  // Use the validate function from useForm to trigger validation
  const { valid: formValid } = await validate(); 
  return (myPhone.value.length === 0 || (formValid && myPhone.value.length === 10));
};

const split = (num) => {
  if (!num || num.length < 10) {
    myPhoneAC.value = '';
    myPhoneF3.value = '';
    myPhoneL4.value = '';
    return;
  }
  myPhoneAC.value = num.substring(0, 3);
  myPhoneF3.value = num.substring(3, 6);
  myPhoneL4.value = num.substring(6, 10);
};

const stitch = ()=> {
  if (invalidPhoneNumChars.value) {
    myPhone.value = '';
  } else {
    myPhone.value = `${myPhoneAC.value}${myPhoneF3.value}${myPhoneL4.value}`;
  }
}

const switchFocus = (e) => {
  const isTab = e.key === 'Tab';
  const isShift = e.key === 'Shift';

  if (!isTab && !isShift) {
    const targetId = e.target.id;
    const targetLength = e.target.value.length;

    switch (targetId) {
      case 'userPhoneAC':
        if (targetLength === 3) {
          nextTick(() => {
            // Use the ref on the Field component to focus the internal input
            f3FieldRef.value?.focus();
          });
        }
      break;
      case 'userPhoneF3':
        if (targetLength === 3) {
          nextTick(() => {
            l4FieldRef.value?.focus();
          });
        }
      break;
    }
  }
};

/**
 * Stop user from entering more digits than allowed. This is necessary when using input type=numbers where
 * there is no native HTML maxlength validation (maxlength is only available for type=text).
 * @param data
 * @returns {*|boolean|string}
 */
const truncateMax = (data) => {
  const partial = data.partial;
  const max = data.max;
  if (partial.length > max) {
    return partial.substring(0, max);
  }
  return partial;
};

const updateMyPhone = (field) => {
  switch (field) {
    case 'myPhoneL4': // go first since the other two fields will normally auto-advance based on length
      myPhoneL4.value = truncateMax({ partial: myPhoneL4.value, max: 4, min: 4 });
      break;
    case 'myPhoneF3':
      myPhoneF3.value = truncateMax({ partial: myPhoneF3.value, max: 3, min: 3 });
      break;
    case 'myPhoneAC':
      myPhoneAC.value = truncateMax({ partial: myPhoneAC.value, max: 3, min: 3 });
      break;
    default:
      // nada
  }
  // construct myPhone
  stitch();
}
defineExpose({
  checkPhone,
  hasValidPhone,
  invalidPhone,
  invalidPhoneNumChars,
  myPhone,
  split
});
</script>


<style lang="scss" scoped>
fieldset.app-embedded-fieldset {
  legend {
    font-size: 1rem;
    padding: 0.5em 0;
  }
}
.app-contact-inputs {
  --box-width: 320px;
  --box-width-tiny: calc(var(--box-width) * 0.85);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  label {
    display: block;
  }
  .app-contact-info {
    width: max-content;
    .app-phone-number {
      input {
        width: 55px;
        display: inline-block;
        padding: 0.5em;
        text-align: center;
      }
      input:last-child {
        width: 65px;
      }
    }
    .app-user-email {
      min-width: var(--box-width);
      ~ p {
        width: var(--box-width);
      }
      @media (max-width: 425px) {
        min-width: var(--box-width-tiny);
        ~ p {
          width: var(--box-width-tiny);
        }
      }
    }
  }
  .app-verification-info {
    .app-verification-number {
      display: inline-block;
      input {
        display: inline-block;
        margin-right: 0.5em;
        padding: 0.25em;
        text-align: center;
        width: 40px;
      }
    }
  }
}
</style>
